import { GridCellParams } from '@mui/x-data-grid-pro';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { map, sortBy, forEach } from 'lodash';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Box, IconButton, Chip, Tooltip } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { Icon, IconType } from '../../Atoms/Icon';
import { Text } from '../../Atoms/Text';
import { ComponentName } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { GridCustomColDef } from '../../types';
import { chipStyles, formatPercent, formatPrice } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { LinkAllocation } from '../../../store/Link';
import { addManualOverride, getAllocationBreakdown, removeManualOverride } from '../../../Api/Link/LinkApi';
import { BasicDataGrid } from '../../Compounds/Table/BasicDataGrid';
import { DetailViewCard } from '../../Compounds/CardWithTitle';
import { Run, RunStatus } from '../../../store/Run';
import { PageLayout } from '../../PageLayout';
import { fetchRpmAccount, getRpmAccountSubject, RpmAccount } from '../../../store/RpmAccount';
import { getCurrentUser, UserRole } from '../../../store/User';
import { AllocationOverrideModal, dataForm } from './AllocationOverrideModal';
import { RunSelection } from '../../Compounds/Run/RunSelection';
import { handleAssetClick } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { RecordLink } from '../../Atoms/RecordLink';

// The API only rejects a one-time override on a SPIFF-only row -- a repeating override, set via
// LinkAllocationSection's addManualOverride without an allocationId, still goes through. Shared
// with AllocationBreakdownPage.test.tsx so the aria-label, Tooltip, and assertions can't drift.
export const SPIFF_ONLY_TOOLTIP = "SPIFF-only allocations can't carry a one-time override.";

const pciceColumns = {
  chargeRecurringExpected: 'Expected MRC',
  chargeRecurringAllocated: 'Allocated MRC',
  revenueRecurringExpected: 'Expected MRR',
  revenueRecurringAllocated: 'Allocated MRR',
  chargeOnetimeExpected: 'Expected NRC',
  chargeOnetimeAllocated: 'Allocated NRC',
  revenueOnetimeExpected: 'Expected NRR',
  revenueOnetimeAllocated: 'Allocated NRR',
  spiffAllocated: 'Allocated SPIFF',
  trueupAllocated: 'Allocated True-Up'
};

const gridCols = (setIsDialogOpenId: React.Dispatch<React.SetStateAction<string | null>>): GridCustomColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      if (params.row.linkId === 'Total') {
        return null;
      }
      const isPAsset = params.row.assetType === 'provisional_asset';
      const type = isPAsset ? ComponentName.PROVISIONAL_ASSET : ComponentName.ASSET;
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            ':hover': {
              cursor: 'pointer'
            }
          }}>
          <Icon
            className="p-3"
            data-testid="detail-cta-icon"
            onClick={() => handleAssetClick(params.row.assetUpstackId, type)}
            type="details"
          />
        </Box>
      );
    },
    hideable: false,
    pinned: 'left',
    disableReorder: true
  },
  {
    headerName: 'Asset',
    field: 'assetNumber',
    renderCell: (params: GridCellParams) => {
      if (params.row.linkId === 'Total') return null;
      let type;
      let iconType;
      if (params.row.assetType === 'provisional_asset') {
        type = 'provisional-asset';
        iconType = 'provisionalAssetMed';
      } else {
        type = 'asset';
        iconType = 'assetMed';
      }
      return (
        <RecordLink
          className="text-indigo flex items-center"
          to={`/${type}/${params.row.assetUpstackId}`}>
          <Icon
            className="p-3 mr-1"
            type={iconType as IconType}
          />
          <Text
            size="sm14"
            color="indigo">
            {params?.value as string}
          </Text>
          {(params.row.assetRpmAccountCount as number) > 1 && (
            <span className="flex items-center ml-2 text-black">
              <ReportProblemIcon
                fontSize="small"
                color="warning"
              />
              +{(params.row.assetRpmAccountCount as number) - 1}
            </span>
          )}
        </RecordLink>
      );
    },
    flex: 1.5,
    type: 'singleSelect',
    filterable: false,
    sortable: false,
    valueOptions: []
  },
  {
    headerName: 'Allocation',
    field: 'allocationPercent',
    align: 'center',
    renderCell: (params: GridCellParams) => {
      if (params.row.linkId === 'Total') return null;
      const value = params.value as number | undefined;
      const isManualOverride = params.row.isManualOverride;
      const className = isManualOverride ? 'bg-dark-orange text-white' : 'bg-gray-100 text-black';
      return <span className={`py-1 px-2 rounded-md ${className}`}>{formatPercent(value, true)}</span>;
    },
    width: 100,
    filterable: false,
    sortable: false
  },
  {
    headerName: '',
    field: 'editPercent',
    align: 'center',
    renderCell: (params: GridCellParams) => {
      if (params.row.linkId === 'Total') return null;
      // A SPIFF-only row that already carries an override (e.g. a date correction moved a
      // previously-normal row into the SPIFF-only window after it was overridden) must stay
      // editable -- removing an override is always allowed by the API; only creating a new
      // one-time override on a SPIFF-only row is rejected.
      const isEditBlocked = params.row.isSpiffOnly && !params.row.isManualOverride;
      const button = (
        <IconButton
          sx={{
            color: 'rgb(107 114 128)'
          }}
          size="small"
          title={isEditBlocked ? undefined : 'Edit'}
          aria-label={isEditBlocked ? SPIFF_ONLY_TOOLTIP : undefined}
          disabled={isEditBlocked}
          onClick={() => setIsDialogOpenId(params.row.id)}>
          <EditNoteIcon />
        </IconButton>
      );
      if (!isEditBlocked) return button;
      return (
        <Tooltip title={SPIFF_ONLY_TOOLTIP}>
          <span>{button}</span>
        </Tooltip>
      );
    },
    width: 50,
    filterable: false,
    sortable: false
  },
  ...map(Object.keys(pciceColumns), (key) => ({
    headerName: pciceColumns[key as keyof typeof pciceColumns],
    field: key,
    renderCell: (params: GridCellParams) => {
      const value = params.value as number | undefined;
      return (
        <span className={`float-right ${value && Number(value) < 0 ? 'text-price-red' : ''}`}>
          {formatPrice(value as number)}
        </span>
      );
    },
    flex: 1,
    filterable: false,
    sortable: false
  })),
  {
    field: 'paymentFrequency',
    headerName: 'Payment Frequency',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    filterable: false,
    flex: 1,
    renderCell: (params: GridCellParams) => {
      const value = params.value as string;
      if (!value) return '';
      return (
        <Chip
          label={value}
          size="small"
          sx={chipStyles(value)}
          title={value}
        />
      );
    }
  }
];

export const AllocationBreakdownPage = () => {
  const { id, monthYear } = useParams();
  const [rpmAccount, setRpmAccount] = useState<RpmAccount | null>(null);
  const [selectedRun, setSelectedRun] = useState<Run | null>(null);
  const [records, setRecords] = useState<LinkAllocation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshState, setRefreshState] = useState<boolean>(false);
  const [isDialogOpenId, setIsDialogOpenId] = useState<string | null>(null);
  const { setSnack } = useSnackbar();
  const navigate = useNavigate();
  const currentUserRole = getCurrentUser()?.role;

  const getRpmAccount = async () => {
    if (!id) return;

    const errorStatusCode = await fetchRpmAccount(id);
    if (errorStatusCode) navigate(`/error/${errorStatusCode}/RPM-account/${id}/${new Date().getTime()}`);
  };

  useEffect(() => {
    getRpmAccount();

    const rpmSub = getRpmAccountSubject().subscribe((d) => setRpmAccount(d));
    return () => {
      if (rpmSub) rpmSub.unsubscribe();
    };
  }, []);

  const isProcessing = selectedRun?.status === RunStatus.PROCESSING;
  const canEdit =
    isProcessing &&
    !!currentUserRole &&
    [UserRole.ADMIN, UserRole.ENGINEER, UserRole.ANALYST].includes(currentUserRole);

  useEffect(() => {
    if (id && selectedRun) {
      fetchData(id, selectedRun.id);
    }
  }, [id, selectedRun]);

  const parseNumber = (value: string | number | null | undefined, decimals = 3): number => {
    if (value === null || value === undefined) return 0;
    return +parseFloat(value as string).toFixed(decimals) || 0;
  };

  const formattingData = (data: LinkAllocation[]) => {
    const totalRecord: LinkAllocation = {
      linkId: '',
      tallyRunId: 0
    } as LinkAllocation;
    forEach(data, (record) => {
      const calcColumns = [...Object.keys(pciceColumns), 'allocationPercent'];
      forEach(calcColumns, (key) => {
        const currentValue = parseNumber(totalRecord[key as keyof LinkAllocation] as string | number);
        const recordValue = parseNumber(record[key as keyof LinkAllocation] as string | number);
        (totalRecord[key as keyof LinkAllocation] as number) = currentValue + recordValue;
      });
    });
    const sortedData = sortBy(data, ['assetNumber']);
    setRecords([...sortedData, { ...totalRecord, linkId: 'Total' }]);
  };

  const fetchData = async (id: string, runId: number, refresh = false) => {
    const loadFunc = refresh ? setRefreshState : setLoading;
    try {
      loadFunc(true);
      const { data, error } = await getAllocationBreakdown(id, runId);
      if (data) formattingData(data);
      if (error) setSnack({ message: error.message, type: 'error', open: true });
    } catch {
      setSnack({ message: 'Error fetching allocation data', type: 'error', open: true });
    } finally {
      loadFunc(false);
    }
  };

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    maxHeight: 'calc(100vh - 320px)',
    height: 'inherit',
    border: 'none',
    borderRadius: '0',
    '.MuiDataGrid-columnHeader': {
      ...DEFAULT_MINI_TABLE_SX_STYLES['.MuiDataGrid-columnHeader'],
      color: '#000000',
      fontWeight: 600
    },
    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell, .MuiDataGrid-overlay': {
      borderRight: '1px solid #E0E0E0'
    },
    '& .MuiDataGrid-columnsContainer': {
      borderBottom: '1px solid #E0E0E0'
    },
    '.MuiDataGrid-columnSeparator': {
      display: 'none'
    },
    '.MuiDataGrid-main': {
      border: '1px solid #E0E0E0',
      borderRight: 'none'
    },
    '.MuiDataGrid-row[data-id="Total"]': {
      backgroundColor: '#F7F7F7'
    },
    '.MuiDataGrid-cell.MuiDataGrid-cell--textCenter': {
      padding: '0 5px'
    }
  };

  const handleSubmitOverride = async (data: dataForm) => {
    if (!isDialogOpenId) return;
    const overrideData = {
      overridePercent: Number(data.allocationPercent),
      repeatOverride: data.repeatOverride,
      allocationId: isDialogOpenId
    };
    const record = records.find((r) => r.id === isDialogOpenId);
    if (!record) return;
    const { error } = await addManualOverride(record.linkId, overrideData);
    if (error) {
      setSnack({ message: error.message, type: 'error', open: true });
    } else {
      setIsDialogOpenId(null);
      if (id && selectedRun) fetchData(id, selectedRun.id, true);
      return true;
    }
  };

  const handleDeleteOverride = async () => {
    if (!isDialogOpenId) return;
    const record = records.find((r) => r.id === isDialogOpenId);
    if (!record) return;
    const { error } = await removeManualOverride(record.linkId, isDialogOpenId);
    if (error) {
      setSnack({ message: error.message, type: 'error', open: true });
    } else {
      setIsDialogOpenId(null);
      if (id && selectedRun) fetchData(id, selectedRun.id, true);
      return true;
    }
  };

  const totalAllocationPercent = records.find((record) => record.linkId === 'Total')?.allocationPercent || 0;
  const isShowWarning = parseNumber(totalAllocationPercent, 2) > 100;
  const pageTitle = `${rpmAccount?.supplier} ${rpmAccount?.accountNumber} Allocation Breakdown`;

  const cols = useMemo(() => gridCols(setIsDialogOpenId), [setIsDialogOpenId]);
  const filteredCols = useMemo(
    () => (canEdit ? cols : cols.filter((col) => col.field !== 'editPercent')),
    [canEdit, cols]
  );

  return (
    <Fragment>
      <div className="mt-4 ml-6 -mb-4">
        <Link
          className="text-indigo inline-block relative"
          title="Fullscreen"
          to={`/rpm-account/${id}`}>
          <div className="flex items-center text-small">
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            &nbsp; RPM Account
          </div>
        </Link>
      </div>
      <PageLayout
        dataTestId="rpm-account-allocation-breakdown-page"
        pageTitle={pageTitle}
        showIcon
        icon="rpmAccountMed">
        <DetailViewCard
          enableCollapse
          key="allocation-mini-table-card"
          title="Allocation"
          dataTestId="allocation-view-card"
          className="mt-4">
          <div
            className="w-full p-4"
            data-testid="allocation-mini-table-container">
            <Box className="flex flex-row items-center gap-2 mb-2">
              <RunSelection
                selectedRunId={selectedRun?.id || null}
                setSelectedRun={setSelectedRun}
                monthYear={monthYear}
                types={[RunStatus.PROCESSING, RunStatus.PENDING_LOCK, RunStatus.LOCKED]}
              />
              {isShowWarning && (
                <div className="flex items-center gap-1 border border-grey-2 px-2 py-2 rounded-lg text-black mb-2">
                  <ErrorIcon
                    fontSize="small"
                    color="red"
                    className="mr-1"
                  />
                  <span className="text-[14px]">Allocation is not 100%</span>
                </div>
              )}
            </Box>
            {loading && <ComponentSpinner label="Loading Allocation Data" />}
            {!loading && (
              <BasicDataGrid
                rows={records}
                columns={filteredCols}
                sxStyle={sxStyle}
                disableColumnMenu
                showColumnVerticalBorder
                showCellVerticalBorder
                primaryKey="linkId"
                dataTestId="allocation-table"
                localLoading={refreshState}
              />
            )}
            <AllocationOverrideModal
              allocation={records.find((record) => record.id === isDialogOpenId) as LinkAllocation}
              open={!!isDialogOpenId}
              setOpen={(state) => {
                if (!state) setIsDialogOpenId(null);
              }}
              handleSubmit={handleSubmitOverride}
              handleDelete={handleDeleteOverride}
            />
          </div>
        </DetailViewCard>
      </PageLayout>
    </Fragment>
  );
};
